// Text measurement shared by every back end, so the editor's preview, the PDF and the ZPL
// agree on where a text fits. Widths are Helvetica's (WinAnsi, per 1000 em) — the PDF uses
// Helvetica itself, the browser preview Arial/Helvetica, and Zebra's scalable font 0 (CG
// Triumvirate) is a Helvetica clone with the same proportions. Characters outside WinAnsi are
// measured as an average glyph. `mono` is Courier at a fixed 600/1000.
// Code points 32–255 in WinAnsi order (generated from @pdf-lib/standard-fonts; 0 = unmapped).
const HELVETICA = [278, 278, 355, 556, 556, 889, 667, 191, 333, 333, 389, 584, 278, 333, 278, 278, 556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 278, 278, 584, 584, 584, 556, 1015, 667, 667, 722, 722, 667, 611, 778, 722, 278, 500, 667, 556, 833, 722, 778, 667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 278, 278, 278, 469, 556, 333, 556, 556, 500, 556, 556, 278, 556, 556, 222, 222, 500, 222, 833, 556, 556, 556, 556, 333, 500, 278, 556, 500, 722, 500, 500, 500, 334, 260, 334, 584, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 278, 333, 556, 556, 556, 556, 260, 556, 333, 737, 370, 556, 584, 333, 737, 333, 400, 584, 333, 333, 333, 556, 537, 278, 333, 333, 365, 556, 834, 834, 834, 611, 667, 667, 667, 667, 667, 667, 1000, 722, 667, 667, 667, 667, 278, 278, 278, 278, 722, 722, 778, 778, 778, 778, 778, 584, 778, 722, 722, 722, 722, 667, 667, 611, 556, 556, 556, 556, 556, 556, 889, 500, 556, 556, 556, 556, 278, 278, 278, 278, 556, 556, 556, 556, 556, 556, 556, 584, 611, 556, 556, 556, 556, 500, 556, 500];
const HELVETICA_BOLD = [278, 333, 474, 556, 556, 889, 722, 238, 333, 333, 389, 584, 278, 333, 278, 278, 556, 556, 556, 556, 556, 556, 556, 556, 556, 556, 333, 333, 584, 584, 584, 611, 975, 722, 722, 722, 722, 667, 611, 778, 722, 278, 556, 722, 611, 833, 722, 778, 667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611, 333, 278, 333, 584, 556, 333, 556, 611, 556, 611, 556, 333, 611, 611, 278, 278, 556, 278, 889, 611, 611, 611, 611, 389, 556, 333, 611, 556, 778, 556, 556, 500, 389, 280, 389, 584, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 278, 333, 556, 556, 556, 556, 280, 556, 333, 737, 370, 556, 584, 333, 737, 333, 400, 584, 333, 333, 333, 611, 556, 278, 333, 333, 365, 556, 834, 834, 834, 611, 722, 722, 722, 722, 722, 722, 1000, 722, 667, 667, 667, 667, 278, 278, 278, 278, 722, 722, 778, 778, 778, 778, 778, 584, 778, 722, 722, 722, 722, 667, 667, 611, 556, 556, 556, 556, 556, 556, 889, 556, 556, 556, 556, 556, 278, 278, 278, 278, 611, 611, 611, 611, 611, 611, 611, 584, 611, 611, 611, 611, 611, 556, 611, 556];
/** Width of one character in 1/1000 em. */
export function glyphWidth(ch, font) {
    if (font.family === "mono")
        return 600;
    const code = ch.codePointAt(0) ?? 32;
    const table = font.bold ? HELVETICA_BOLD : HELVETICA;
    if (code >= 32 && code < 256) {
        const w = table[code - 32];
        if (w > 0)
            return w;
    }
    // Outside WinAnsi (e.g. “ ” – …): an average glyph.
    if (code === 0x2013)
        return 556; // en dash
    if (code === 0x2014)
        return 1000;
    if (code === 0x2026)
        return 1000;
    if (code === 0x201c || code === 0x201d || code === 0x2018 || code === 0x2019)
        return 333;
    return font.bold ? 611 : 556;
}
/** Width of `text` in points at `size` points. */
export function textWidthPt(text, font, size = font.size) {
    let w = 0;
    for (const ch of text)
        w += glyphWidth(ch, font);
    return (w / 1000) * size;
}
/** Width in mm. */
export function textWidthMm(text, font, size = font.size) {
    return textWidthPt(text, font, size) / (72 / 25.4);
}
/** Greedy word wrap into lines that fit `widthMm`; a word longer than the line is broken. */
export function wrapText(text, font, size, widthMm) {
    const out = [];
    for (const para of text.split(/\r?\n/)) {
        const words = para.split(/\s+/).filter((w) => w.length > 0);
        if (words.length === 0) {
            out.push("");
            continue;
        }
        let line = "";
        for (const word of words) {
            const candidate = line ? `${line} ${word}` : word;
            if (textWidthMm(candidate, font, size) <= widthMm || !line) {
                if (!line && textWidthMm(word, font, size) > widthMm) {
                    // Break the long word by characters.
                    let piece = "";
                    for (const ch of word) {
                        if (textWidthMm(piece + ch, font, size) > widthMm && piece) {
                            out.push(piece);
                            piece = ch;
                        }
                        else
                            piece += ch;
                    }
                    line = piece;
                }
                else
                    line = candidate;
            }
            else {
                out.push(line);
                line = word;
            }
        }
        out.push(line);
    }
    return out;
}
/** The largest size ≤ `max` (≥ `min`) at which `text` fits `widthMm` on one line, or wrapped into
 *  `heightMm` when `wrap` is set. */
export function fitSize(text, font, widthMm, heightMm, lineHeight, wrap, max, min = 3) {
    const fitsAt = (size) => {
        if (!wrap)
            return textWidthMm(text, font, size) <= widthMm && size * lineHeight / (72 / 25.4) <= heightMm + 0.01;
        const lines = wrapText(text, font, size, widthMm);
        return lines.length * size * lineHeight / (72 / 25.4) <= heightMm + 0.01;
    };
    if (fitsAt(max))
        return max;
    let lo = min, hi = max;
    for (let i = 0; i < 24 && hi - lo > 0.1; i++) {
        const mid = (lo + hi) / 2;
        if (fitsAt(mid))
            lo = mid;
        else
            hi = mid;
    }
    return Math.max(min, Math.floor(lo * 10) / 10);
}
//# sourceMappingURL=metrics.js.map